import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum } from '@/shared/interfaces';
import { useAuthStore } from '@/store/auth';
import { useUserModulesStore } from '@/store/auth';
import type {
  IModuloPermiso,
  IModuloRol,
  IModuloRolCreate,
  IUsuarioERPPaginado,
  IUsuarioRolAsignacion,
  IUsuarioRolPaginado,
  IUsuarioAreaAsignacion,
  IUsuarioAreaPaginado,
  IUsuarioPermisosResumen,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { useUiStore } from '@/store/ui';

import {
  USE_MOCK,
  MOCK_PERMISOS,
  MOCK_ROLES,
  MOCK_USUARIOS_ERP,
  MOCK_USUARIOS_ROL,
  MOCK_USUARIOS_AREA,
  mockAxiosResponse,
} from './tickets-mock-data';

const { get, post, put, remove } = erpApi();

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/admin/`;

// === Permisos ===

export const useFetchPermisos = (modulo?: string) => {
  return useQuery({
    queryKey: ['modulo-permisos', modulo],
    queryFn: async () => {
      if (USE_MOCK) {
        const filtered = modulo
          ? MOCK_PERMISOS.filter((p) => p.modulo === modulo)
          : MOCK_PERMISOS;
        return mockAxiosResponse(filtered);
      }
      const params = modulo ? `?modulo=${modulo}` : '';
      return get<IModuloPermiso[]>(`${BASE_URL}permisos/${params}`, true);
    },
    staleTime: 1000 * 60 * 5,
  });
};

// === Roles ===

export const useFetchRoles = () => {
  return useQuery({
    queryKey: ['modulo-roles'],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_ROLES);
      return get<IModuloRol[]>(`${BASE_URL}roles/`, true);
    },
  });
};

export const useFetchRol = (id: string) => {
  return useQuery({
    queryKey: ['modulo-roles', id],
    queryFn: async () => {
      if (USE_MOCK) {
        const rol = MOCK_ROLES.find((r) => r.id_rol === id) || MOCK_ROLES[0];
        return mockAxiosResponse(rol);
      }
      return get<IModuloRol>(`${BASE_URL}roles/${id}/`, true);
    },
    enabled: !!id,
  });
};

export const useCreateRol = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (data: IModuloRolCreate) => {
      return post<IModuloRol>(`${BASE_URL}roles/`, data, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modulo-roles'] });
      setAlert?.(true, 'Rol creado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useUpdateRol = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<IModuloRolCreate> }) => {
      return put<IModuloRol>(`${BASE_URL}roles/${id}/`, data, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modulo-roles'] });
      setAlert?.(true, 'Rol actualizado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useDeleteRol = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: string) => {
      return remove(`${BASE_URL}roles/${id}/`, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modulo-roles'] });
      setAlert?.(true, 'Rol eliminado', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// === Usuarios ERP ===

export const useFetchUsuariosERP = (search: string, enabled = true, page = 1, pageSize = 50) => {
  return useQuery({
    queryKey: ['usuarios-erp', search, page, pageSize],
    queryFn: async () => {
      if (USE_MOCK) {
        const filtered = MOCK_USUARIOS_ERP.filter(
          (u) => u.nombre_completo.toLowerCase().includes(search.toLowerCase()) ||
            u.username.toLowerCase().includes(search.toLowerCase())
        );
        return mockAxiosResponse({ data: filtered, total: filtered.length, page: 1, page_size: 50, pages: 1 });
      }
      return get<IUsuarioERPPaginado>(
        `${BASE_URL}usuarios-erp/?q=${encodeURIComponent(search)}&page=${page}&page_size=${pageSize}`,
        true
      );
    },
    enabled: enabled && search.length >= 2,
  });
};

/**
 * Lista los usuarios asignables por el supervisor en el modal "Asignar"
 * / "Reasignar". El backend identifica al supervisor por el header
 * X-User-Id inyectado por el api-gateway.
 *
 * Reglas actuales (refactor: ahora usa api2_usuario_area como unica
 * fuente de verdad, coherente con el resto del modulo):
 *   - Areas del supervisor desde api2_usuario_area (las activas).
 *   - Usuarios activos asignados a cualquiera de esas areas.
 *   - Se excluye al propio supervisor.
 *   - is_active = true en Django.
 *   - YA NO se filtra por profile.rol ni por profile.departamento_id.
 *
 * Requiere permiso tickets.seleccionar_agentes_escalamiento en el rol
 * del supervisor para poder llamar al endpoint.
 */
export const useFetchAgentesMiDepartamento = (
  search: string = '',
  enabled = true,
  page = 1,
  pageSize = 50,
) => {
  return useQuery({
    queryKey: ['agentes-mi-departamento', search, page, pageSize],
    queryFn: async () => {
      if (USE_MOCK) {
        const filtered = MOCK_USUARIOS_ERP.filter(
          (u) => u.nombre_completo.toLowerCase().includes(search.toLowerCase()) ||
            u.username.toLowerCase().includes(search.toLowerCase()),
        );
        return mockAxiosResponse({
          data: filtered, total: filtered.length, page: 1, page_size: pageSize, pages: 1,
        });
      }
      return get<IUsuarioERPPaginado>(
        `${BASE_URL}usuarios-erp/agentes-mi-departamento/?q=${encodeURIComponent(search)}&page=${page}&page_size=${pageSize}`,
        true,
      );
    },
    // No exigimos search >=2 porque queremos mostrar todos los agentes del
    // departamento al abrir el modal, aunque el usuario no escriba nada.
    enabled,
  });
};

/**
 * Hook user-centric para los tabs de Gestion de Accesos. A diferencia de
 * useFetchUsuariosERP, este NO requiere busqueda minima — devuelve la pagina
 * completa de usuarios del ERP siempre que esta habilitado. Soporta el flag
 * `with_assignments` para que el backend incluya el rol y area de tickets
 * actualmente asignados a cada usuario (joineado en bloque).
 */
export interface UsuariosERPListParams {
  q?: string;
  page?: number;
  page_size?: number;
  with_assignments?: boolean;
  enabled?: boolean;
}

export const useFetchUsuariosERPList = (params: UsuariosERPListParams = {}) => {
  const {
    q = '',
    page = 1,
    page_size = 50,
    with_assignments = false,
    enabled = true,
  } = params;

  return useQuery({
    queryKey: ['usuarios-erp-list', q, page, page_size, with_assignments],
    queryFn: async () => {
      if (USE_MOCK) {
        const filtered = MOCK_USUARIOS_ERP.filter(
          (u) =>
            !q ||
            u.nombre_completo.toLowerCase().includes(q.toLowerCase()) ||
            u.username.toLowerCase().includes(q.toLowerCase())
        );
        return mockAxiosResponse({
          data: filtered,
          total: filtered.length,
          page: 1,
          page_size,
          pages: 1,
        });
      }
      const sp = new URLSearchParams();
      sp.set('q', q);
      sp.set('page', String(page));
      sp.set('page_size', String(page_size));
      if (with_assignments) sp.set('with_assignments', 'true');
      return get<IUsuarioERPPaginado>(
        `${BASE_URL}usuarios-erp/?${sp.toString()}`,
        true
      );
    },
    enabled,
    placeholderData: (prev) => prev,
  });
};

export const useUpdateUsuarioERP = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, any> }) => {
      return erpApi().patch<any>(`${BASE_URL}usuarios-erp/${id}/`, data, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-erp'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-rol'] });
      setAlert?.(true, 'Usuario actualizado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// === Usuario-Rol ===

export const useFetchUsuariosRol = (params?: { usuario_id?: number; rol_id?: string; q?: string; page?: number; page_size?: number }) => {
  return useQuery({
    queryKey: ['usuarios-rol', params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse({ data: MOCK_USUARIOS_ROL, total: MOCK_USUARIOS_ROL.length, page: 1, page_size: 50, pages: 1 });
      const searchParams = new URLSearchParams();
      if (params?.usuario_id) searchParams.set('usuario_id', String(params.usuario_id));
      if (params?.rol_id) searchParams.set('rol_id', params.rol_id);
      if (params?.q) searchParams.set('q', params.q);
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.page_size) searchParams.set('page_size', String(params.page_size));
      return get<IUsuarioRolPaginado>(
        `${BASE_URL}usuarios-rol/?${searchParams.toString()}`,
        true
      );
    },
  });
};

export const useAsignarRolUsuario = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (data: {
      usuario_id: number;
      usuario_username: string;
      usuario_nombre_completo: string;
      rol: string;
    }) => {
      return post<IUsuarioRolAsignacion>(`${BASE_URL}usuarios-rol/`, data, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-rol'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-erp-list'] });
      setAlert?.(true, 'Rol asignado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useEliminarRolUsuario = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: string) => {
      return remove(`${BASE_URL}usuarios-rol/${id}/`, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-rol'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-erp-list'] });
      setAlert?.(true, 'Asignación eliminada', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// === Usuario-Area ===

export const useFetchUsuariosArea = (params?: { usuario_id?: number; area_id?: string; q?: string; page?: number; page_size?: number }) => {
  return useQuery({
    queryKey: ['usuarios-area', params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse({ data: MOCK_USUARIOS_AREA, total: MOCK_USUARIOS_AREA.length, page: 1, page_size: 50, pages: 1 });
      const searchParams = new URLSearchParams();
      if (params?.usuario_id) searchParams.set('usuario_id', String(params.usuario_id));
      if (params?.area_id) searchParams.set('area_id', params.area_id);
      if (params?.q) searchParams.set('q', params.q);
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.page_size) searchParams.set('page_size', String(params.page_size));
      return get<IUsuarioAreaPaginado>(
        `${BASE_URL}usuarios-area/?${searchParams.toString()}`,
        true
      );
    },
  });
};

export const useAsignarAreaUsuario = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (data: {
      usuario_id: number;
      usuario_username: string;
      usuario_nombre_completo: string;
      area: string;
      es_responsable?: boolean;
      /** Check "Configuración": solo acceso al árbol del área en Catálogos,
       *  sin recibir tickets ni bandeja (pensado para admin TI). */
      es_configuracion?: boolean;
    }) => {
      return post<IUsuarioAreaAsignacion>(`${BASE_URL}usuarios-area/`, data, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-area'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-erp-list'] });
      setAlert?.(true, 'Área asignada correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

/**
 * PUT /usuarios-area/{id}/ — Actualiza una asignacion existente. Util para
 * toggle de los flags es_responsable / es_configuracion sin tener que borrar
 * y recrear la fila.
 */
export const useActualizarAreaUsuario = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (data: {
      id_usuario_area: string;
      es_responsable?: boolean;
      es_configuracion?: boolean;
      activo?: boolean;
    }) => {
      const { id_usuario_area, ...payload } = data;
      return put<IUsuarioAreaAsignacion>(
        `${BASE_URL}usuarios-area/${id_usuario_area}/`,
        payload,
        true,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-area'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-erp-list'] });
      setAlert?.(true, 'Asignación actualizada', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

export const useEliminarAreaUsuario = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (id: string) => {
      return remove(`${BASE_URL}usuarios-area/${id}/`, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-area'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-erp-list'] });
      setAlert?.(true, 'Asignación eliminada', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// === Resumen permisos usuario ===

export const useFetchUsuarioResumen = (userId: number, enabled = true) => {
  return useQuery({
    queryKey: ['usuario-resumen-permisos', userId],
    queryFn: async () => {
      if (USE_MOCK) {
        return mockAxiosResponse({
          usuario_id: userId,
          roles: ['rol-002'],
          permisos: ['tickets.ver', 'tickets.crear', 'tickets.editar'],
          areas: [{ id: 'area-001', nombre: 'Soporte N1', es_responsable: false }],
        });
      }
      return get<IUsuarioPermisosResumen>(
        `${BASE_URL}usuarios/${userId}/resumen/`,
        true
      );
    },
    enabled: enabled && !!userId,
  });
};

// === Usuarios por área ===

export interface IUsuarioArea {
  id: number;
  username: string;
  nombre_completo: string;
  es_responsable: boolean;
  es_configuracion?: boolean;
  area_nombre: string;
}

export const useFetchUsuariosPorArea = (areaId: string, enabled = true) => {
  return useQuery({
    queryKey: ['usuarios-por-area', areaId],
    queryFn: async () => {
      if (USE_MOCK) {
        return mockAxiosResponse([
          { id: 1, username: 'supervisor1', nombre_completo: 'Supervisor Ejemplo', es_responsable: true, area_nombre: 'Soporte N1' },
          { id: 2, username: 'agente1', nombre_completo: 'Agente Ejemplo', es_responsable: false, area_nombre: 'Soporte N1' },
        ]);
      }
      return get<IUsuarioArea[]>(`${BASE_URL}areas/${areaId}/usuarios/`, true);
    },
    enabled: enabled && !!areaId,
  });
};

// === Sincronización de datos ERP ===

export const useSincronizarDatos = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async () => {
      return post<any>(`${BASE_URL}sincronizar/`, {}, true);
    },
    onSuccess: (res) => {
      const data = (res as any)?.data || {};
      const msg = `Usuarios: ${data.usuarios || '?'} | Áreas: ${data.areas || '?'} | Clientes: ${data.clientes || '?'}`;
      setAlert?.(true, `Sincronización completada. ${msg}`, AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// === Módulos del sistema por usuario ===

export const useFetchUsuarioModulos = (userId: number, enabled = true) => {
  return useQuery({
    queryKey: ['usuario-modulos', userId],
    queryFn: async () => {
      if (USE_MOCK) {
        return mockAxiosResponse({
          usuario_id: userId,
          modulos: [
            'TICKETS_SOPORTE_CLIENTES.NUEVO_TICKET',
            'TICKETS_SOPORTE_CLIENTES.MIS_TICKETS',
            'TICKETS_SOPORTE_CLIENTES.CATALOGOS',
            'TICKETS_SOPORTE_CLIENTES.GESTION_ACCESOS',
          ],
        });
      }
      return get<{ usuario_id: number; modulos: string[] }>(
        `${BASE_URL}usuarios/${userId}/modulos/`,
        true
      );
    },
    refetchOnWindowFocus: false,
    enabled: enabled && !!userId,
  });
};

export const useUpdateUsuarioModulos = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ userId, modulos }: { userId: number; modulos: string[] }) => {
      return put<{ usuario_id: number; modulos: string[] }>(
        `${BASE_URL}usuarios/${userId}/modulos/`,
        { modulos },
        true
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['usuario-modulos', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-erp'] });
      queryClient.invalidateQueries({ queryKey: ['usuarios-erp-list'] });

      // Actualizar store local si el usuario modificado es el logueado
      syncModulesIfCurrentUser(variables.userId, variables.modulos);

      setAlert?.(true, 'Módulos actualizados correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => handleAxiosError(error),
  });
};

// === Sincronización de módulos en tiempo real ===

/**
 * Actualiza el store de módulos si el userId coincide con el usuario logueado.
 */
function syncModulesIfCurrentUser(userId: number, modulos: string[]) {
  const currentUserId = useAuthStore.getState().user?.user_id;
  if (currentUserId && currentUserId === userId) {
    useUserModulesStore.getState().setModules(modulos);
  }
}

/**
 * Hook que sincroniza los módulos del usuario logueado cuando la ventana
 * recupera el foco. Esto permite que si un admin cambia los módulos de
 * otro usuario, ese usuario vea los cambios al volver a la pestaña sin
 * necesidad de cerrar sesión.
 *
 * Montar una sola vez en un componente de alto nivel (ej: AppRouter o Layout).
 */
export const useModulesAutoSync = () => {
  useEffect(() => {
    const handleFocus = async () => {
      try {
        const currentUserId = useAuthStore.getState().user?.user_id;
        if (!currentUserId) return;

        const res: any = await get(
          `${BASE_URL}usuarios/${currentUserId}/modulos/`,
          true
        );
        const modulos: string[] = res?.data?.modulos || res?.modulos || [];
        if (modulos.length > 0) {
          const currentModules = useUserModulesStore.getState().modules;
          const changed =
            modulos.length !== currentModules.length ||
            modulos.some((m: string) => !currentModules.includes(m));
          if (changed) {
            useUserModulesStore.getState().setModules(modulos);
          }
        }
      } catch {
        // Silenciar errores de sync - no interrumpir al usuario
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);
};
